/**
 * The derivations behind `/failures` — the console's view of what the SOFTWARE
 * is doing wrong, as opposed to the audit trail's view of what OPERATORS did.
 *
 * Only type imports, on the precedent `analytics-window.ts`, `analytics-users.ts`
 * and `device-metadata.ts` set: Node strips types, so a module with no value
 * imports and no JSX runs directly under `node`, and the self-check at the foot
 * of this file is what `pnpm --filter @empite/admin-portal test` executes. The screen
 * itself is `failures.tsx`; this is everything on it that is not JSX.
 *
 * Everything here exists because it would be wrong SILENTLY if it drifted, and
 * every one of them is the same species of wrong: a screen that reports the
 * platform as healthy when it is not.
 *
 *   * ONE ROW IS ONE FAULT, NOT ONE FAILURE. `ops.system_failures` is keyed on
 *     a fingerprint, so a repeat increments `occurrence_count` instead of
 *     adding a row. A table that draws fifty rows and says "fifty failures"
 *     understates an incident by whatever the counts happen to be.
 *   * AN EMPTY LIST IS FOUR DIFFERENT FACTS. Nothing is failing; nothing
 *     matches this filter; nothing has been acknowledged; there is no fault on
 *     record at all. Only the first is good news, and drawing all four the same
 *     way is how the good news gets read into the other three.
 *   * A CUT PAGE IS NOT A COMPLETE ONE. The server reports `truncated` even
 *     when false precisely so the two are tellable apart, and the screen has to
 *     spend that.
 */
import type { BadgeTone } from "@empite/ui-components";
import type { SeriesPoint } from "@empite/ui-components";
import type {
  Failure,
  FailureAcknowledgedFilter,
  FailureAcknowledgement,
  FailureDayCount,
  FailureGroupCount,
  FailureKind,
  FailurePage,
  FailureQuery,
  FailureService,
  FailureSeverity,
  FailureSummary,
  FailureWindow,
} from "../admin-client";

/**
 * The grant `/failures` is drawn from, named here only to decide whether to
 * draw it. The Admin API guards all three read routes with it and writes each
 * refusal to the audit trail; hiding the screen is a courtesy, not a control.
 *
 * Four of the five roles hold it — every one but `billing_ops`, whose
 * money-shaped failures already have `/wallets`'s settlement list behind
 * `wallets:read`.
 */
export const FAILURES_READ = "failures:read";

/**
 * What the URL carries. Every field is a plain string because that is what a
 * hand-written link carries, and none of them is narrowed to its vocabulary
 * here — see `failureQueryFrom`.
 */
export interface FailuresSearch {
  service?: string;
  kind?: string;
  severity?: string;
  acknowledged?: string;
  since?: string;
  until?: string;
  user?: string;
  device?: string;
  application?: string;
}

/**
 * The three closed vocabularies, in the order `internal/ops` declares them
 * rather than alphabetically: severity runs from least to most consequential,
 * so a select reads as a scale, and a chart of these values in a later story
 * puts its bars in the same places twice.
 *
 * Repeated from the wire types on purpose. `FailureService` and friends are
 * unions, which exist only at compile time — a `<select>` needs the values at
 * RUN time and there is no way to derive one from the other. What keeps the two
 * copies honest is `NoMissingOption` below, in BOTH directions: `satisfies`
 * refuses a value here that the union does not have, and the `Exclude` refuses
 * a union member that has no option here. The second is the one that matters —
 * a filter value the console cannot reach has no run-time evidence at all.
 */
export const FAILURE_SEVERITIES = ["warning", "error", "critical"] as const satisfies
  readonly FailureSeverity[];

export const FAILURE_SERVICES = [
  "platform-api",
  "worker",
  "fallback-inference",
  "edge",
  "admin-api",
] as const satisfies readonly FailureService[];

export const FAILURE_KINDS = [
  "http_5xx",
  "panic",
  "job_failed",
  "provider_error",
  "settlement_failed",
  "reconciliation_required",
  "migration",
  "other",
] as const satisfies readonly FailureKind[];

/**
 * The three sides of the acknowledgement, with what each one MEANS rather than
 * what it is called on the wire.
 *
 * "false" is first and is the default: the console's job is the open set. The
 * labels say what a list under each one is a list OF, because "true" as a
 * heading is a filter value and "Already dealt with" is an answer.
 */
export const ACKNOWLEDGED_OPTIONS = [
  { value: "false", label: "Still open" },
  { value: "true", label: "Already dealt with" },
  { value: "all", label: "Open and cleared" },
] as const satisfies readonly { value: FailureAcknowledgedFilter; label: string }[];

/**
 * The wholeness assertion, and it is a TYPE rather than a check.
 *
 * `AssertNever` only accepts `never`, so this alias stops compiling the day a
 * member is added to one of the four wire unions and not to the array above it.
 * That is the failure with no run-time symptom: an option missing from a select
 * is a filter nobody can reach, and the screen just quietly never offers it —
 * so `pnpm -r typecheck` is the only place it can be caught.
 *
 * Exported so it is not an unused local, and erasable, so type stripping
 * removes it before Node ever sees this file.
 */
type AssertNever<T extends never> = T;

export type NoMissingOption = AssertNever<
  | Exclude<FailureSeverity, (typeof FAILURE_SEVERITIES)[number]>
  | Exclude<FailureService, (typeof FAILURE_SERVICES)[number]>
  | Exclude<FailureKind, (typeof FAILURE_KINDS)[number]>
  | Exclude<FailureAcknowledgedFilter, (typeof ACKNOWLEDGED_OPTIONS)[number]["value"]>
>;

/**
 * The URL as the client's filter.
 *
 * The four vocabulary fields are carried through AS WRITTEN and are not
 * narrowed to the sets above. That is deliberate and it is the same rule the
 * analytics window follows: the Admin API refuses a value the column could not
 * hold with a 400 naming the accepted set, and dropping it here instead would
 * answer the UNFILTERED question under a heading the operator believes says
 * `?service=worker`. "Nothing is failing in the worker" is the most dangerous
 * wrong answer this screen can give, and it is the one a client-side narrowing
 * produces. The selects cannot emit anything outside the vocabulary anyway, so
 * the only caller that reaches this path is a hand-edited link — which gets the
 * server's refusal, in the screen's error state.
 *
 * `limit` is not sent at all. The server's default page is 50 and its ceiling
 * is 200; a page size is not a filter and has no business in a URL an operator
 * pastes into a ticket, and the response reports the size it resolved to, which
 * is what the truncation notice is built from.
 */
export function failureQueryFrom(search: FailuresSearch): FailureQuery {
  return {
    service: search.service as FailureService | undefined,
    kind: search.kind as FailureKind | undefined,
    severity: search.severity as FailureSeverity | undefined,
    acknowledged: search.acknowledged as FailureAcknowledgedFilter | undefined,
    since: search.since,
    until: search.until,
    user: search.user,
    device: search.device,
    application: search.application,
  };
}

/** Which parameter a chip belongs to, so removing one is a navigation with
 *  that single key cleared rather than nine call sites each clearing its own. */
export type FailureFilterField = keyof FailuresSearch;

export interface FilterChip {
  field: FailureFilterField;
  /** What the narrowing is, in the operator's words. */
  label: string;
  /** The value itself, rendered as `<code>` beside the label. */
  value: string;
}

/**
 * Everything the current URL narrows by, as removable chips.
 *
 * They exist because two of the nine filters — `user` and `device` — have no
 * control in the form: they are uuids, arrived at by following "every fault on
 * this account" out of a row, and a free-text uuid box would be a 400 waiting
 * to be typed. A filter with no control and no chip would be a narrowing an
 * operator cannot see and cannot undo, which is the shape of "the console says
 * nothing is failing".
 *
 * `acknowledged` appears only when it is not the default. The server's default
 * IS `"false"`, so a chip saying "still open" on an unfiltered page would
 * describe the absence of a decision as a decision.
 */
export function filterChips(search: FailuresSearch): FilterChip[] {
  const chips: FilterChip[] = [];
  if (search.severity) {
    chips.push({ field: "severity", label: "severity", value: search.severity });
  }
  if (search.service) {
    chips.push({ field: "service", label: "service", value: search.service });
  }
  if (search.kind) {
    chips.push({ field: "kind", label: "kind", value: search.kind });
  }
  if (search.application) {
    chips.push({ field: "application", label: "application", value: search.application });
  }
  if (search.user) {
    chips.push({ field: "user", label: "account", value: search.user });
  }
  if (search.device) {
    chips.push({ field: "device", label: "installation", value: search.device });
  }
  if (search.since) {
    chips.push({ field: "since", label: "first seen on or after", value: search.since });
  }
  if (search.until) {
    chips.push({ field: "until", label: "first seen on or before", value: search.until });
  }
  if (search.acknowledged && search.acknowledged !== "false") {
    chips.push({ field: "acknowledged", label: "acknowledgement", value: search.acknowledged });
  }
  return chips;
}

/**
 * Whether the URL narrows the SET, as opposed to choosing which side of the
 * acknowledgement to look at.
 *
 * The distinction is the whole reason `emptyTitle` takes both: `?service=worker`
 * with no rows means "not this one"; `?acknowledged=true` with no rows means
 * "nobody has cleared anything", which is a different sentence about a
 * different question.
 */
export function isNarrowed(search: FailuresSearch): boolean {
  return filterChips(search).some((chip) => chip.field !== "acknowledged");
}

/**
 * Clearing ONE filter, for the chips above the table.
 *
 * Every key survives as an explicit `undefined` rather than being deleted, so
 * what goes to `navigate` is always the whole search object. TanStack merges
 * what it is given; a key that is simply absent would be left at its previous
 * value, and a chip that does not remove its own filter is worse than no chip.
 */
export function withoutFilter(search: FailuresSearch, field: FailureFilterField): FailuresSearch {
  const next: FailuresSearch = { ...search };
  next[field] = undefined;
  return next;
}

/**
 * No filter at all — the URL the nav link points at and the "show everything"
 * control produces.
 *
 * Spelled out rather than `{}` for the same reason `withoutFilter` keeps its
 * keys: every one has to be explicitly cleared. It doubles as the one place a
 * filter added later must be listed, and the compiler says so.
 */
export const NO_FILTER: { [K in keyof Required<FailuresSearch>]: undefined } = {
  service: undefined,
  kind: undefined,
  severity: undefined,
  acknowledged: undefined,
  since: undefined,
  until: undefined,
  user: undefined,
  device: undefined,
  application: undefined,
};

export interface ApplicationOption {
  value: string;
  label: string;
}

/**
 * The application select's options, with the CURRENT value guaranteed to be one
 * of them.
 *
 * A select whose value matches no option renders as its first option, which
 * here would silently redraw `?application=app-b` as "Any application" — the
 * control saying the list is unfiltered while the list is not. Three ways that
 * happens, and all three are handled:
 *
 *   * the catalogue has not loaded yet, so there are no options at all;
 *   * the filter arrived as an application's UUID (the route takes either), and
 *     the options are keyed by slug;
 *   * the filter names something the catalogue does not have, which the server
 *     answers with a 404 — the screen still has to show what was asked.
 *
 * Options are keyed by SLUG because that is what an operator recognises in a
 * pasted URL, and because the route accepts it.
 */
export function applicationOptions(
  current: string | undefined,
  applications: readonly { id: string; slug: string; name: string }[],
): ApplicationOption[] {
  const options = applications.map((app) => ({ value: app.slug, label: `${app.name} (${app.slug})` }));
  if (!current || options.some((option) => option.value === current)) {
    return options;
  }
  const byID = applications.find((app) => app.id === current);
  return [
    byID
      ? { value: current, label: `${byID.name} (${byID.slug})` }
      : { value: current, label: `${current} — not in this catalogue` },
    ...options,
  ];
}

/**
 * Which of the five things the table can be saying.
 *
 * FIVE, where the acceptance criteria name four. `listed` and `truncated` are
 * genuinely different answers — "these are the faults matching this filter" and
 * "these are the newest N of more than N" — and collapsing them is exactly the
 * failure `truncated` is reported for. The four the criteria name are all here
 * and all distinct.
 *
 * ORDER MATTERS, and it is the order of what the operator can act on. `failed`
 * beats `loading` only when there is NOTHING behind it: an error over a page
 * that is still on screen is a REFRESH that failed, and the rows below it are
 * real and stay — the failure is said above them rather than instead of them.
 * `empty` beats `truncated` because the server cannot cut a page it found no
 * rows for; if the two ever disagree, the honest render is the one that draws
 * no rows.
 */
export type FailureListState = "loading" | "failed" | "empty" | "listed" | "truncated";

export function failureListState(
  failed: boolean,
  hasPage: boolean,
  rows: number,
  truncated: boolean,
): FailureListState {
  if (!hasPage) {
    return failed ? "failed" : "loading";
  }
  if (rows === 0) {
    return "empty";
  }
  return truncated ? "truncated" : "listed";
}

/**
 * What an empty answer MEANS, which depends on the question that was asked.
 *
 * `acknowledged` is the RESPONSE's echoed value rather than the URL's, so a
 * page titled from a filter the server resolved differently is impossible.
 */
export function emptyTitle(acknowledged: FailureAcknowledgedFilter, narrowed: boolean): string {
  if (narrowed) {
    return "No fault matches this filter";
  }
  switch (acknowledged) {
    case "true":
      return "Nothing has been acknowledged";
    case "all":
      return "There is no fault on record";
    default:
      return "Nothing is failing";
  }
}

/**
 * The body under that title, and it branches on the SAME two inputs.
 *
 * It has to. A title that says "Nothing has been acknowledged" over a body that
 * says "an empty list here is the whole platform" is a screen making the good
 * claim anyway, in the paragraph an operator actually reads — the render-proof
 * for this story found exactly that, twice.
 */
export function emptyBody(acknowledged: FailureAcknowledgedFilter, narrowed: boolean): string {
  if (narrowed) {
    return "Nothing on record matches every part of this filter. Remove one of the narrowings above to widen it — an empty list under a filter is a statement about the filter, and not about the platform.";
  }
  switch (acknowledged) {
    case "true":
      return "Nothing has been taken off this console. That is not an empty log: it means no fault on record has been acknowledged by anybody, so every one of them is still open. Ask for the open set to see them.";
    case "all":
      return "Not one fault, open or cleared, has ever been recorded. On a deployment that has been serving traffic for a while that is worth a second look — it is also exactly what a recorder nobody wired up would produce.";
    default:
      return "No unacknowledged fault is on record. Every deployment writes to this log — platform-api, the worker, fallback inference, the edge and both back-office APIs — so this is the whole platform rather than one service. Cleared faults are still there; ask for them by acknowledgement.";
  }
}

/**
 * The nav badge: how many faults nobody has dealt with.
 *
 * Four renders, not two, and the fourth is the reason this is a function rather
 * than a ternary in the layout:
 *
 *   * a read that FAILED shows `?`, not nothing. A badge that disappears when
 *     the failure log cannot be read tells an operator glancing at the nav that
 *     nothing is failing — which is the one thing this badge exists to never
 *     say by accident.
 *   * a read still IN FLIGHT shows nothing. A nav that flickers on every screen
 *     change is a nav an operator stops reading.
 *   * an EMPTY open set shows nothing. Zero is good news and a "0" beside every
 *     other link is noise.
 *   * anything else shows the count, with a `+` when the page was cut. The
 *     badge counts the ROWS THAT CAME BACK, which is at most the page size —
 *     the list route returns no total, and inventing one from a page would be a
 *     number with no source.
 *
 * The tone is `danger` for any open fault regardless of severity. The rows on
 * hand are the newest page rather than the whole set, so "the worst severity
 * that is open" is not a question this data can answer, and a badge coloured
 * from a sample would be confident about the wrong thing.
 */
export interface NavBadge {
  text: string;
  tone: BadgeTone;
  /** The `title`, which is also the accessible name — the count alone is a
   *  number with no noun. */
  title: string;
}

export function navBadge(
  failed: boolean,
  hasPage: boolean,
  rows: number,
  truncated: boolean,
): NavBadge | null {
  if (!hasPage) {
    return failed
      ? {
          text: "?",
          tone: "warning",
          title: "The failure log could not be read, so how many faults are open is unknown.",
        }
      : null;
  }
  if (rows <= 0) {
    return null;
  }
  const counted = truncated ? `${group(rows)}+` : group(rows);
  return {
    text: counted,
    tone: "danger",
    title: truncated
      ? `More than ${group(rows)} faults are open. The badge counts one page; the screen says how many more.`
      : `${group(rows)} ${rows === 1 ? "fault is" : "faults are"} open and unacknowledged.`,
  };
}

/**
 * `occurrence_count` as a sentence.
 *
 * The count is the difference between something that happened once at four in
 * the morning and something that is happening right now, several times a
 * second, and the row looks identical either way — which is the whole hazard of
 * a fingerprinted table. The number gets a column of its own AND this sentence
 * as the cell's accessible name, because "10,000" in a narrow column reads as
 * an id.
 */
export function occurrenceLabel(count: number): string {
  if (count === 1) {
    return "fired once";
  }
  if (count === 2) {
    return "fired twice";
  }
  return `fired ${group(count)} times`;
}

/** The bare figure, grouped. Separate from the sentence above because the cell
 *  draws the number large and says the sentence quietly. */
export function occurrenceCount(count: number): string {
  return group(count);
}

/**
 * Severity is neither status nor health, so it does not share `statusTone` or
 * `healthTone`.
 *
 * `critical` and `error` both draw as danger, and that is not a loss: the word
 * itself is in the badge, and the pair is "somebody outside got nothing" versus
 * "a process died or money is at stake" — both are the row an operator is
 * scanning for. `warning` is the platform having ABSORBED something, which is
 * an act-before-it-matters row and must not look like the other two.
 */
export function severityTone(severity: string): BadgeTone {
  switch (severity) {
    case "critical":
    case "error":
      return "danger";
    case "warning":
      return "warning";
    default:
      return "neutral";
  }
}

/**
 * What a row's acknowledgement says, in the three states it has.
 *
 * `recurred` is the one worth spelling out. An acknowledged row that has fired
 * again keeps its acknowledgement on purpose — a noisy fault would otherwise
 * undo an operator's decision several times a second — so the recurrence has no
 * effect on the row's own state and would be invisible without being said.
 */
export function acknowledgementNote(failure: {
  acknowledged: boolean;
  recurred: boolean;
}): string {
  if (!failure.acknowledged) {
    return "open";
  }
  return failure.recurred ? "cleared, and has fired again since" : "cleared";
}

/**
 * The window a list ran over, in words.
 *
 * "Unbounded" is a real and deliberate answer here rather than a missing
 * filter, and it is the difference between this route and every other windowed
 * read in the console: an unacknowledged fault that started months ago must
 * stay on screen, so the list defaults to no window at all and lets the page
 * size bound it. Saying so is what stops an operator assuming the last thirty
 * days.
 */
export function describeWindow(window: FailureWindow): string {
  if (!window.bounded) {
    return "Every fault on record — no date filter.";
  }
  const span = window.days === null ? "" : ` (${group(window.days)} ${window.days === 1 ? "day" : "days"})`;
  return `Faults first seen from ${window.since} to ${window.until}${span}.`;
}

/**
 * The scrubbed context as TEXT, two-space indented.
 *
 * A string, and deliberately nothing richer. It goes into a `<pre>` as a text
 * child, React escapes it, and no key inside the document can choose how it is
 * drawn. The recorder scrubs this before it is stored, but the console is where
 * an operator holding every permission in the realm is signed in, and a
 * renderer that walked the object would be handing whatever produced the
 * failure authorship of the console's own markup.
 *
 * Undefined becomes `{}` rather than the string "undefined": the list route
 * omits `context` entirely and the detail route always sends an object, so the
 * only way to reach that branch is a document that disagrees with its own
 * contract.
 */
export function contextText(context: Record<string, unknown> | undefined): string {
  return JSON.stringify(context ?? {}, null, 2);
}

/** `{}` is what the recorder stores when a failure carried no structured
 *  detail, and it is a fact rather than an absence — so the panel says which
 *  one it is looking at instead of drawing an empty box. */
export function contextIsEmpty(context: Record<string, unknown> | undefined): boolean {
  return context === undefined || Object.keys(context).length === 0;
}

/**
 * Which of the three things ONE expanded row can be saying.
 *
 * There is no "not asked" member, unlike `MetadataReadState`: the query is
 * mounted only while the row is open, so a closed row has no state to report.
 * The three that remain are still worth an enumeration rather than two
 * booleans, because the failing one has to win over the pending one — a detail
 * read that 404s (a row swept by retention while the page was open) must not
 * spin for ever.
 */
export type FailureDetailState = "loading" | "failed" | "ready";

export function failureDetailState(failed: boolean, hasDetail: boolean): FailureDetailState {
  if (hasDetail) {
    return "ready";
  }
  return failed ? "failed" : "loading";
}

/**
 * Thousands separators, hand-rolled rather than `toLocaleString`, which answers
 * differently depending on the box's locale — including the box the self-check
 * below runs on.
 *
 * A copy of `device-metadata.ts`'s, and copied on purpose: these modules run as
 * `node src/screens/<file>.ts`, and Node needs a file extension it can resolve,
 * which the compiler's own extensionless specifiers do not give it. One shared
 * helper would cost both files their ability to run at all.
 */
function group(value: number): string {
  const digits = String(value);
  let out = "";
  for (let i = 0; i < digits.length; i += 1) {
    if (i > 0 && (digits.length - i) % 3 === 0) {
      out += ",";
    }
    out += digits[i];
  }
  return out;
}

// ---------------------------------------------------------------------------
// The charts, and the two acts that take a row off the console
// ---------------------------------------------------------------------------

/**
 * The grant that CLEARS a fault, which is a strictly smaller set of holders
 * than the one that reads the log: four roles look, two may take a row off the
 * default view.
 *
 * Every control built on it is ABSENT rather than disabled — the tick boxes as
 * well as the two buttons, because a selection an operator cannot act on is a
 * column of controls that does nothing. The Admin API re-checks the grant and
 * audits the refusal; hiding it only spares the click.
 */
export const FAILURES_CLEAR = "failures:clear";

/**
 * The severity stack, BOTTOM FIRST — which is the opposite of the select's
 * order, and deliberately so.
 *
 * The bottom of a stack is the only part that shares a baseline across every
 * bar, so it is the only part a reader can actually compare day to day. That
 * place belongs to the severity somebody is scrolling this screen to find.
 * `FAILURE_SEVERITIES` runs low-to-high because a select reads as a scale; a
 * stack is not a scale, it is a composition, and putting `warning` on the
 * baseline would make the one severity that matters least the one measurable
 * at a glance.
 *
 * Colour is left to the palette's own slots. The severities are three
 * categories rather than a good/bad pair, and the meaning is carried by stack
 * POSITION and by the legend's words as well as by hue — which is the rule for
 * this palette, whose red and green slots a deuteranope cannot separate.
 */
export const SEVERITY_STACK = ["critical", "error", "warning"] as const satisfies
  readonly FailureSeverity[];

/** The stack has to name every severity: one left out is a bar drawn short
 *  with nothing saying which part is missing. Same assertion, and the same
 *  reason, as `NoMissingOption` above. */
export type NoMissingStackSeverity = AssertNever<
  Exclude<FailureSeverity, (typeof SEVERITY_STACK)[number]>
>;

export interface DaySeries {
  labels: string[];
  series: { name: string; points: SeriesPoint[] }[];
}

/**
 * The daily series, split by severity, ready for `StackedBarChart`.
 *
 * NEW FAULTS PER DAY, not failures per day. A row is filed under the day the
 * fault was FIRST seen — `counts_basis` says so on the wire — so a fault that
 * started in March and is still firing several times a second is one unit in
 * March's bar and nothing at all in today's. The card says that out loud,
 * because a stacked column chart of a failure log is read as "how much broke
 * today" by everybody who has ever seen one.
 *
 * Every day the server sent is a bar, including the quiet ones: the response
 * carries the whole window rather than only the days with rows, so a zero here
 * is a measured zero and there are no gaps to invent.
 */
export function severityDaySeries(days: readonly FailureDayCount[]): DaySeries {
  return {
    labels: days.map((day) => day.day),
    series: SEVERITY_STACK.map((severity) => ({
      name: severity,
      points: days.map((day) => ({ label: day.day, value: day[severity] })),
    })),
  };
}

/**
 * Days whose three parts do not add up to the total the same document carries.
 *
 * The parts and the total come off one source on the server precisely so they
 * cannot disagree — which means a disagreement here is a document at odds with
 * itself, and the honest thing is to say so rather than to draw the parts and
 * let the reader assume the bar is the total. Empty on every well-formed
 * answer, so the note it feeds never fires in practice.
 */
export function disagreeingDays(days: readonly FailureDayCount[]): string[] {
  return days
    .filter((day) => day.warning + day.error + day.critical !== day.total)
    .map((day) => day.day);
}

/**
 * One breakdown as chart points.
 *
 * `measure` is not a default, and that is the point: `faults` and
 * `occurrences` are two different numbers about the same rows — how many
 * distinct things are broken, and how many times those things have fired — and
 * a chart that picked one silently would be captioned with whichever the reader
 * assumed. The screen names the measure in the card's heading and passes it
 * here, so the two cannot drift.
 *
 * A value nothing produced is ABSENT from the response rather than a zero row,
 * so there is nothing to filter: a bar chart of eight labelled nothings is not
 * a picture of anything.
 */
export function groupPoints(
  groups: readonly FailureGroupCount[],
  measure: "faults" | "occurrences",
): SeriesPoint[] {
  return groups.map((group) => ({
    label: group.value,
    value: measure === "faults" ? group.faults : group.occurrences,
  }));
}

/**
 * Whether two reads ran over the same days.
 *
 * The list and the summary resolve their windows DIFFERENTLY on purpose — the
 * summary defaults to thirty days because a daily series has to name the days
 * it counts, the list defaults to no window at all so an unacknowledged fault
 * from months ago stays on the console — so putting them on one screen means
 * putting two answers to two questions side by side. Both report what they
 * used; this is the comparison, and the note below is what it feeds.
 */
export function sameWindow(a: FailureWindow, b: FailureWindow): boolean {
  return a.bounded === b.bounded && a.since === b.since && a.until === b.until;
}

/**
 * What to say when the charts and the table are not looking at the same days.
 *
 * Null when they agree, which is what a URL carrying explicit dates produces.
 * The common case — no dates at all — is a disagreement, and it is the one an
 * operator would otherwise never suspect: the charts look like the table's
 * summary and are a strictly smaller window.
 */
export function windowMismatchNote(list: FailureWindow, charts: FailureWindow): string | null {
  if (sameWindow(list, charts)) {
    return null;
  }
  const drawn = charts.bounded
    ? `${charts.since} to ${charts.until}`
    : "every fault on record";
  const tabled = list.bounded ? `${list.since} to ${list.until}` : "every fault on record";
  return `The charts cover ${drawn}; the table below covers ${tabled}. A daily series has to name the days it counts, so the summary always resolves a window and the list does not — a fault that started before the charts begin is in the table and in none of the bars.`;
}

/**
 * Which of the four things the charts can be saying.
 *
 * `quiet` is its own state and is NOT "nothing is failing": the summary always
 * resolves a window, so an empty one is a claim about those days and about
 * nothing else. The list beside it can be — and routinely is — full of rows
 * that started earlier.
 */
export type FailureChartState = "loading" | "failed" | "quiet" | "ready";

export function failureChartState(
  failed: boolean,
  hasSummary: boolean,
  faults: number,
): FailureChartState {
  if (!hasSummary) {
    return failed ? "failed" : "loading";
  }
  return faults <= 0 ? "quiet" : "ready";
}

/**
 * The headline pair, in the words the wire's two `*_basis` members oblige.
 *
 * Two numbers that a fingerprinted table cannot reconcile, so they get two
 * names and a stated basis: `faults` is how many distinct things are broken in
 * this window, `occurrences` is how many times those things have fired EVER,
 * including before the window started. A screen that printed the second under
 * a windowed heading would be overstating the window by whatever the counts
 * happen to be.
 */
export function describeTotals(summary: FailureSummary): string {
  const faults = `${group(summary.faults)} ${summary.faults === 1 ? "fault" : "faults"}`;
  const fired = `${group(summary.occurrences)} ${summary.occurrences === 1 ? "time" : "times"}`;
  return `${faults} started in this window, and the faults matching this filter have fired ${fired} in total. The second figure is a LIFETIME count — the table keeps no per-day occurrence history — so it includes firings from before these days.`;
}

// ---------------------------------------------------------------------------
// Clearing
// ---------------------------------------------------------------------------

/**
 * The one sentence both clear controls say, and it is said in the dialog AND
 * in the panel.
 *
 * An operator pressing a red button called "Clear" in a back office has every
 * reason to think something is being deleted, and the reason dialog is the last
 * place they read anything before it happens. Nothing in this realm can delete
 * a failure — admin_api holds no DELETE grant on the table, the retention sweep
 * runs as the worker — so the honest word is HIDES.
 */
export const CLEARING_HIDES =
  "Clearing HIDES these rows from the default view; it does not delete them. Nothing is removed from the table: each row keeps every column it had and gains who cleared it, when, and why. Set the acknowledgement filter to “Already dealt with” or “Open and cleared” to see them again.";

/** Where a clear's row count came from. `floor` is the one that is not exact:
 *  the page was cut and no other read can be trusted to describe the same set. */
export type ClearBasis = "page" | "summary" | "floor";

export interface ClearScope {
  /** How many rows the act will IDENTIFY, whatever state they are in. */
  matched: number;
  /** How many of those it will actually WRITE. Null when it cannot be derived
   *  from what is on screen — never zero standing in for "unknown", which is a
   *  real count of a thing that would mean the act is pointless. */
  clears: number | null;
  /** False when `matched` is a floor rather than the figure. */
  exact: boolean;
  basis: ClearBasis;
}

/**
 * How many rows "clear everything this filter matches" would touch.
 *
 * The act uses the LIST's filter, resolved by the list's own parser, so the
 * page in front of the operator is the honest source whenever it is complete:
 * an uncut page IS the matching set, and the rows on it that are still open are
 * exactly the rows that will be written.
 *
 * When the page is cut, the summary can still answer — but only when it ran
 * over the same window and the same side of the acknowledgement, which the
 * default filter makes untrue. Two documents from two routes have two clamps;
 * borrowing a figure across that boundary would put the last thirty days'
 * count under a sentence about every fault on record.
 *
 * Everything else is a FLOOR and says so. A number that might be wrong is worse
 * than no number on a confirmation, and the server reports what it actually did
 * — and refuses the act outright above its own ceiling.
 */
export function clearScope(
  page: FailurePage | undefined,
  summary: FailureSummary | undefined,
): ClearScope | null {
  if (!page) {
    return null;
  }
  const rows = page.failures;
  if (!page.truncated) {
    return {
      matched: rows.length,
      clears: rows.filter((row) => !row.acknowledged).length,
      exact: true,
      basis: "page",
    };
  }
  if (summary && sameWindow(summary.window, page.window) && summary.acknowledged === page.acknowledged) {
    return {
      matched: summary.faults,
      // Derived from the SIDE the filter asked for rather than counted: the
      // open set is entirely open, the cleared set is entirely cleared, and
      // "all" is the only one this document cannot split.
      clears: clearsWithin(page.acknowledged, summary.faults),
      exact: true,
      basis: "summary",
    };
  }
  return { matched: rows.length, clears: null, exact: false, basis: "floor" };
}

/** How many of a matched set an acknowledgement writes, given which side of
 *  the acknowledgement the set is. Null for `all`, which mixes the two. */
function clearsWithin(acknowledged: FailureAcknowledgedFilter, matched: number): number | null {
  switch (acknowledged) {
    case "true":
      return 0;
    case "all":
      return null;
    default:
      return matched;
  }
}

/**
 * The confirmation's sentence, and the panel's — ONE derivation, used twice.
 *
 * The acceptance criterion is that the exact number appears in the dialog and
 * on the screen, and the way two figures in two places come to disagree is by
 * being computed twice. So they are computed once, here, and the caller decides
 * only where to put the string.
 */
export function describeClearScope(scope: ClearScope): string {
  if (!scope.exact) {
    return `This page is cut, so how many faults the filter matches is not knowable from here — it is more than the ${group(scope.matched)} drawn below. Narrow the filter until the whole answer fits on one page if you want to see the number before you clear it; the server reports what it actually cleared either way, and refuses the act outright above its own ceiling.`;
  }
  const matched = `${group(scope.matched)} ${scope.matched === 1 ? "fault" : "faults"}`;
  if (scope.clears === null) {
    return `This filter matches ${matched}. Some already carry somebody else's acknowledgement and will keep it; the answer will say how many were actually cleared.`;
  }
  if (scope.clears === 0) {
    return `This filter matches ${matched}, and every one of them has already been dealt with. Clearing again would write nothing — an acknowledgement is never overwritten, so the original operator's decision stands.`;
  }
  if (scope.clears === scope.matched) {
    return `This will clear ${matched} — every row the filter matches, and every one of them is still open.`;
  }
  const open = group(scope.clears);
  const left = scope.matched - scope.clears;
  const kept =
    left === 1
      ? "the other one already carries somebody else's decision and keeps it"
      : `the other ${group(left)} already carry somebody else's decision and keep it`;
  return `This filter matches ${matched}, of which ${open} ${scope.clears === 1 ? "is" : "are"} still open. Clearing writes ${scope.clears === 1 ? "that one" : `those ${open}`}; ${kept}.`;
}

/** The selection's sentence. Exact by construction — these are rows the
 *  operator ticked, on the page in front of them. */
export function describeSelection(count: number): string {
  if (count === 1) {
    return "This will clear the one fault you have ticked.";
  }
  return `This will clear the ${group(count)} faults you have ticked.`;
}

/**
 * What a completed acknowledgement did, in all three of its numbers.
 *
 * "Nine cleared" hides the two facts an operator most needs: how many rows the
 * ask found at all, and how many were somebody else's decision already. A
 * colleague having got there first is invisible without the third figure, and
 * it is the difference between "that is done" and "somebody else is already on
 * this incident".
 */
export function describeAcknowledgement(result: FailureAcknowledgement): string {
  const parts: string[] = [];
  parts.push(
    result.acknowledged === 0
      ? `Nothing was cleared: the ask matched ${group(result.matched)} ${result.matched === 1 ? "fault" : "faults"}.`
      : `Cleared ${group(result.acknowledged)} of the ${group(result.matched)} ${result.matched === 1 ? "fault" : "faults"} the ask matched.`,
  );
  if (result.already_acknowledged > 0) {
    parts.push(
      `${group(result.already_acknowledged)} already carried somebody else's acknowledgement and kept it — an acknowledgement is never overwritten.`,
    );
  }
  // Null on the filter form, where it would be a real count of something that
  // cannot happen: a filter cannot name a row that does not exist.
  if (result.missing !== null && result.missing > 0) {
    parts.push(
      `${group(result.missing)} named no row at all — most likely swept by retention while this page was open.`,
    );
  }
  return parts.join(" ");
}

/**
 * The refusal above the server's own ceiling, read off the problem document.
 *
 * A 422 carrying `matched` and `max_matched`, which the route puts on the wire
 * so the console does not keep a copy of the number in step by remembering.
 * Both are read defensively: a proxy's error page classifies as `internal` with
 * no members at all, and a banner built on `undefined` would say "matches
 * faults, which is more than a".
 *
 * The caller does the `isApiError` narrowing and hands the primitives in.
 * `isApiError` is an `instanceof`, so it cannot live in this module — which has
 * no value imports at all, so that it runs under `node` as a self-check.
 */
export interface ClearRefusal {
  matched: number | null;
  ceiling: number | null;
  /** The server's own sentence, which already names both figures. Kept because
   *  it is the authoritative wording and the two numbers above may be absent. */
  message: string;
}

export function clearRefusal(
  status: number,
  code: string,
  message: string,
  problem: unknown,
): ClearRefusal | null {
  if (status !== 422 || code !== "failed_precondition") {
    return null;
  }
  return {
    matched: extensionNumber(problem, "matched"),
    ceiling: extensionNumber(problem, "max_matched"),
    message,
  };
}

/** One numeric extension member of an RFC 9457 document, or null. The document
 *  is whatever arrived on the wire, so nothing about its shape is assumed. */
function extensionNumber(problem: unknown, key: string): number | null {
  if (typeof problem !== "object" || problem === null) {
    return null;
  }
  const value = (problem as Record<string, unknown>)[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** The refusal in the operator's words, with the next step in it. The server's
 *  own sentence carries both figures already; this adds what to do about it. */
export function describeRefusal(refusal: ClearRefusal): string {
  const ceiling =
    refusal.ceiling === null
      ? "the ceiling"
      : `the ${group(refusal.ceiling)} a single acknowledgement may clear`;
  const matched =
    refusal.matched === null
      ? "This filter matches more faults than"
      : `This filter matches ${group(refusal.matched)} faults, more than`;
  return `${matched} ${ceiling}. NOTHING WAS WRITTEN. Narrow the filter — by service, by kind, or by a date range — and clear it in pieces: clearing an ongoing incident in one act is how it stops being visible.`;
}

/**
 * Whether an ask is already known to be over the ceiling.
 *
 * The ceiling is only knowable from a response, so this answers false until the
 * screen has seen one refusal — at which point the remembered figure is used to
 * warn INSIDE the confirmation, before a second act that would be refused the
 * same way. A floor is enough to decide it: "more than N" is over the ceiling
 * whenever N already is.
 */
export function overCeiling(scope: ClearScope | null, ceiling: number | null): boolean {
  if (!scope || ceiling === null) {
    return false;
  }
  return scope.matched > ceiling;
}

// ---------------------------------------------------------------------------
// The selection
// ---------------------------------------------------------------------------

/**
 * Which rows on the page can be ticked at all.
 *
 * Only the OPEN ones. A tick box on a row that already carries somebody's
 * acknowledgement offers an act that writes nothing — the server reports it as
 * `already_acknowledged` and the original decision stands — and it would make
 * "the exact number of rows that will be affected" a number the screen has to
 * qualify. Absent instead, with the row's own note saying it is cleared.
 */
export function selectableIDs(rows: readonly Failure[]): string[] {
  return rows.filter((row) => !row.acknowledged).map((row) => row.id);
}

/**
 * The ticks that are still on screen, in ROW ORDER.
 *
 * Derived in the render rather than corrected in an effect, and intersected
 * with the page rather than trusted: the list refetches on its own schedule and
 * after every acknowledgement, and a selection carrying an id the operator can
 * no longer see is precisely the act this screen exists to prevent — clearing a
 * row nobody looked at.
 */
export function effectiveSelection(
  selected: ReadonlySet<string>,
  rows: readonly Failure[],
): string[] {
  return selectableIDs(rows).filter((id) => selected.has(id));
}

/** A tick added or removed. A new Set, because the caller holds it in state. */
export function toggleSelected(
  current: ReadonlySet<string>,
  id: string,
  on: boolean,
): Set<string> {
  const next = new Set(current);
  if (on) {
    next.add(id);
  } else {
    next.delete(id);
  }
  return next;
}

/** How the header's tick box draws: none, some, or all of the selectable rows.
 *  A page with nothing selectable is `none` — an "all" that stands for zero
 *  rows is a control claiming an act it cannot perform. */
export type SelectAllState = "none" | "some" | "all";

export function selectAllState(chosen: number, selectable: number): SelectAllState {
  if (selectable <= 0 || chosen <= 0) {
    return "none";
  }
  return chosen >= selectable ? "all" : "some";
}

/**
 * A page can never hold more rows than the id ceiling allows.
 *
 * `MaxAcknowledgeIDs` is the list's own maximum page size — 200, deliberately
 * the same number — so "tick everything on this page" is structurally incapable
 * of being refused for length. Asserted rather than assumed: the day the two
 * stop agreeing, the select-all control starts producing a 400 nobody predicted.
 */
export function selectionWithinCeiling(chosen: number, maxIDs: number | null): boolean {
  return maxIDs === null || chosen <= maxIDs;
}

// ---------------------------------------------------------------------------
// Self-check — `node src/screens/failures.ts`, wired into `make test-ts`
// ---------------------------------------------------------------------------

export function selfCheck(): void {
  let passed = 0;
  const check = (name: string, actual: unknown, expected: unknown): void => {
    const show = (value: unknown): string =>
      JSON.stringify(value, (_key, item) => (item === undefined ? "<undefined>" : item)) ??
      "<undefined>";
    if (show(actual) !== show(expected)) {
      throw new Error(
        `failure-log self-check FAILED: ${name}\n  expected ${show(expected)}\n  actual   ${show(actual)}`,
      );
    }
    passed += 1;
  };

  // ---- the vocabularies ---------------------------------------------------
  // Sizes, not contents. Both directions of the contents are the compiler's job
  // (`NoMissingOption` above); what these pin is that growing a vocabulary is a
  // deliberate act in two places rather than a value that appeared.
  check("three severities", FAILURE_SEVERITIES.length, 3);
  check("five services", FAILURE_SERVICES.length, 5);
  check("eight kinds", FAILURE_KINDS.length, 8);
  check("three acknowledgement sides", ACKNOWLEDGED_OPTIONS.length, 3);
  // Severity reads as a scale, least consequential first. Alphabetical would
  // put `critical` at the top of the select, above `error`.
  check("severity runs low to high", FAILURE_SEVERITIES[2], "critical");
  check("the open set is the first option", ACKNOWLEDGED_OPTIONS[0].value, "false");
  // `other` is IN the vocabulary and is deliberately last: a failure nobody
  // anticipated has to be recordable, because those are the interesting ones.
  check("other is a kind", FAILURE_KINDS[FAILURE_KINDS.length - 1], "other");

  // ---- failureQueryFrom ---------------------------------------------------
  check("an empty URL filters by nothing", failureQueryFrom({}), {
    service: undefined,
    kind: undefined,
    severity: undefined,
    acknowledged: undefined,
    since: undefined,
    until: undefined,
    user: undefined,
    device: undefined,
    application: undefined,
  });
  check("every field is carried", failureQueryFrom({
    service: "worker",
    kind: "job_failed",
    severity: "error",
    acknowledged: "all",
    since: "2026-08-01",
    until: "2026-08-28",
    user: "u-1",
    device: "d-1",
    application: "app-a",
  }), {
    service: "worker",
    kind: "job_failed",
    severity: "error",
    acknowledged: "all",
    since: "2026-08-01",
    until: "2026-08-28",
    user: "u-1",
    device: "d-1",
    application: "app-a",
  });
  // THE POINT OF THE FUNCTION: a value outside the vocabulary is sent, not
  // dropped. Dropped, it would answer the unfiltered question under a heading
  // claiming a filter — "nothing is failing in the worker" for a link that
  // said `?service=wroker`. Sent, it is a 400 naming the accepted set.
  check(
    "a misspelt service is carried to the server",
    failureQueryFrom({ service: "wroker" }).service,
    "wroker",
  );
  // A page size is not a filter and never travels in the URL.
  check("no limit is ever sent", "limit" in failureQueryFrom({ service: "worker" }), false);

  // ---- filterChips / isNarrowed -------------------------------------------
  check("an empty URL has no chips", filterChips({}), []);
  // The server's own default. A chip for it would present the absence of a
  // decision as a decision the operator made.
  check("the default acknowledgement is not a chip", filterChips({ acknowledged: "false" }), []);
  check("a non-default acknowledgement is a chip", filterChips({ acknowledged: "all" }), [
    { field: "acknowledged", label: "acknowledgement", value: "all" },
  ]);
  // The two filters with no control in the form. Without a chip they would be
  // a narrowing an operator can neither see nor undo.
  check("a user filter is a chip", filterChips({ user: "u-1" }), [
    { field: "user", label: "account", value: "u-1" },
  ]);
  check("a device filter is a chip", filterChips({ device: "d-1" }), [
    { field: "device", label: "installation", value: "d-1" },
  ]);
  check(
    "chips come in the order the form reads",
    filterChips({ kind: "panic", severity: "critical", service: "worker" }).map((c) => c.field),
    ["severity", "service", "kind"],
  );
  check("nothing narrows an empty URL", isNarrowed({}), false);
  check("the acknowledgement alone does not narrow", isNarrowed({ acknowledged: "all" }), false);
  check("a service narrows", isNarrowed({ service: "worker" }), true);
  check("a user narrows", isNarrowed({ user: "u-1" }), true);
  // Both together: the acknowledgement must not mask a real narrowing.
  check("a narrowing beside the acknowledgement still narrows", isNarrowed({ acknowledged: "all", kind: "panic" }), true);

  // ---- withoutFilter / NO_FILTER ------------------------------------------
  check("clearing a filter keeps the key", withoutFilter({ service: "worker" }, "service"), {
    service: undefined,
  });
  check("clearing one filter leaves the others", withoutFilter({ service: "worker", kind: "panic" }, "kind"), {
    service: "worker",
    kind: undefined,
  });
  // Clearing something that was never set is a no-op that still names the key.
  check("clearing an unset filter", withoutFilter({}, "user"), { user: undefined });
  check("nothing is filtered by nothing", filterChips(NO_FILTER), []);
  check("the empty filter names every parameter", Object.keys(NO_FILTER).length, 9);

  // ---- applicationOptions -------------------------------------------------
  const catalogue = [
    { id: "id-a", slug: "app-a", name: "App A" },
    { id: "id-b", slug: "app-b", name: "App B" },
  ];
  check("no filter, the whole catalogue", applicationOptions(undefined, catalogue), [
    { value: "app-a", label: "App A (app-a)" },
    { value: "app-b", label: "App B (app-b)" },
  ]);
  check("a slug that is in the catalogue adds nothing", applicationOptions("app-b", catalogue).length, 2);
  // The catalogue has not arrived. Without this the select would render as
  // "Any application" over a list that is filtered to one.
  check("a filter with no catalogue keeps itself", applicationOptions("app-b", []), [
    { value: "app-b", label: "app-b — not in this catalogue" },
  ]);
  // The route takes a uuid too, and the options are keyed by slug — so the
  // value has to be kept AND named, or the operator sees a bare uuid.
  check("a uuid is resolved to its name", applicationOptions("id-b", catalogue)[0], {
    value: "id-b",
    label: "App B (app-b)",
  });
  check("a uuid does not drop the catalogue", applicationOptions("id-b", catalogue).length, 3);
  // A 404 waiting to happen, and the control must still show what was asked.
  check("something the catalogue does not have", applicationOptions("gone", catalogue)[0], {
    value: "gone",
    label: "gone — not in this catalogue",
  });

  // ---- failureListState ---------------------------------------------------
  check("nothing yet is loading", failureListState(false, false, 0, false), "loading");
  check("a failure with no page is the whole answer", failureListState(true, false, 0, false), "failed");
  // A refresh that failed over a page still on screen. Reporting it as `failed`
  // would deny the rows below it, which are real.
  check("a failed refresh over a page still lists", failureListState(true, true, 3, false), "listed");
  check("no rows is empty", failureListState(false, true, 0, false), "empty");
  check("rows are listed", failureListState(false, true, 3, false), "listed");
  check("a cut page is its own state", failureListState(false, true, 50, true), "truncated");
  // The server cannot cut a page it found no rows for; if the two ever
  // disagree, the render that draws no rows is the honest one.
  check("empty beats truncated", failureListState(false, true, 0, true), "empty");

  // ---- emptyTitle ---------------------------------------------------------
  check("the good answer", emptyTitle("false", false), "Nothing is failing");
  check("nothing cleared", emptyTitle("true", false), "Nothing has been acknowledged");
  check("nothing at all", emptyTitle("all", false), "There is no fault on record");
  // A narrowed filter overrides all three: "nothing is failing" is a claim
  // about the PLATFORM, and a filtered list is not evidence for it.
  check("a filter beats the good answer", emptyTitle("false", true), "No fault matches this filter");
  check("a filter beats the cleared answer", emptyTitle("true", true), "No fault matches this filter");
  check("a filter beats the whole-record answer", emptyTitle("all", true), "No fault matches this filter");
  // The body branches on the same two inputs as the title. It has to: a title
  // saying "nothing has been acknowledged" over a body saying "this is the
  // whole platform" makes the good claim anyway, in the sentence that gets
  // read. Four distinct bodies, and nothing shared between them.
  {
    const bodies = [
      emptyBody("false", false),
      emptyBody("true", false),
      emptyBody("all", false),
      emptyBody("false", true),
    ];
    check("four empty answers, four bodies", new Set(bodies).size, 4);
    // Only the good answer may say the word "unacknowledged" about the whole
    // platform, and only the narrowed one may blame the filter.
    check("the narrowed body blames the filter", emptyBody("all", true), emptyBody("false", true));
    check("the open-set body is about the platform", emptyBody("false", false).includes("the whole platform"), true);
    check("the acknowledged body is not", emptyBody("true", false).includes("the whole platform"), false);
    check("the whole-record body is not", emptyBody("all", false).includes("the whole platform"), false);
  }

  // ---- navBadge -----------------------------------------------------------
  check("a read in flight shows nothing", navBadge(false, false, 0, false), null);
  check("an empty open set shows nothing", navBadge(false, true, 0, false), null);
  // The render this function exists for: a badge that vanished on a failed read
  // would report a healthy platform from a read that never happened.
  check("a failed read shows a question mark", navBadge(true, false, 0, false)?.text, "?");
  check("a failed read is not danger-toned", navBadge(true, false, 0, false)?.tone, "warning");
  check("a count", navBadge(false, true, 7, false)?.text, "7");
  check("a count is danger-toned", navBadge(false, true, 7, false)?.tone, "danger");
  check("a cut page says more", navBadge(false, true, 50, true)?.text, "50+");
  check("the badge groups", navBadge(false, true, 1234, false)?.text, "1,234");
  check(
    "one fault reads as one fault",
    navBadge(false, true, 1, false)?.title,
    "1 fault is open and unacknowledged.",
  );
  check(
    "two faults read as two",
    navBadge(false, true, 2, false)?.title,
    "2 faults are open and unacknowledged.",
  );
  // A failed read over a page that is still cached is the same refresh-failure
  // case as the list's, and the cached count is the better answer than `?`.
  check("a failed refresh keeps the count", navBadge(true, true, 4, false)?.text, "4");

  // ---- occurrenceLabel / occurrenceCount ----------------------------------
  check("once", occurrenceLabel(1), "fired once");
  check("twice", occurrenceLabel(2), "fired twice");
  check("many", occurrenceLabel(3), "fired 3 times");
  // The number this whole column exists for. One row saying "1" and one row
  // saying this are the same height on the screen.
  check("ten thousand", occurrenceLabel(10000), "fired 10,000 times");
  check("the bare figure groups", occurrenceCount(10000), "10,000");
  check("the bare figure of one", occurrenceCount(1), "1");

  // ---- severityTone -------------------------------------------------------
  check("critical is danger", severityTone("critical"), "danger");
  check("error is danger", severityTone("error"), "danger");
  // The one that must NOT look like the other two: the platform absorbed it.
  check("warning is warning", severityTone("warning"), "warning");
  // A severity this console has not been taught is neutral rather than
  // guessed at — a new rung drawn as `danger` would be a colour with no
  // meaning behind it.
  check("an unknown severity is neutral", severityTone("catastrophic"), "neutral");

  // ---- acknowledgementNote ------------------------------------------------
  check("an open fault", acknowledgementNote({ acknowledged: false, recurred: false }), "open");
  check("a cleared fault", acknowledgementNote({ acknowledged: true, recurred: false }), "cleared");
  // The state with no column of its own: the acknowledgement survives a
  // repeat, so a fault that has come back looks exactly like one that has not.
  check(
    "a cleared fault that came back",
    acknowledgementNote({ acknowledged: true, recurred: true }),
    "cleared, and has fired again since",
  );
  // `recurred` is false for an open row by construction — a fault nobody has
  // dealt with has not recurred, it is still open — and the note must not
  // start inventing a recurrence if that ever changes.
  check(
    "an open fault is never a recurrence",
    acknowledgementNote({ acknowledged: false, recurred: true }),
    "open",
  );

  // ---- describeWindow -----------------------------------------------------
  check(
    "an unbounded list says so",
    describeWindow({ bounded: false, since: null, until: null, days: null, clamped: false }),
    "Every fault on record — no date filter.",
  );
  check(
    "a bounded list names its days",
    describeWindow({ bounded: true, since: "2026-08-01", until: "2026-08-28", days: 28, clamped: false }),
    "Faults first seen from 2026-08-01 to 2026-08-28 (28 days).",
  );
  check(
    "one day is one day",
    describeWindow({ bounded: true, since: "2026-08-28", until: "2026-08-28", days: 1, clamped: false }),
    "Faults first seen from 2026-08-28 to 2026-08-28 (1 day).",
  );
  // A bounded window with no span is a document that disagrees with itself;
  // the dates are still the answer and are still printed.
  check(
    "a bounded window with no span still reads",
    describeWindow({ bounded: true, since: "2026-08-01", until: "2026-08-28", days: null, clamped: false }),
    "Faults first seen from 2026-08-01 to 2026-08-28.",
  );

  // ---- contextText / contextIsEmpty ---------------------------------------
  check("context indents", contextText({ attempt: 3 }), '{\n  "attempt": 3\n}');
  check("an absent context is an empty document", contextText(undefined), "{}");
  check("an empty context is empty", contextIsEmpty({}), true);
  check("an absent context is empty", contextIsEmpty(undefined), true);
  check("a context with a key is not empty", contextIsEmpty({ attempt: 3 }), false);
  // The point of the whole file, again: anything trying to be markup comes
  // back as the CHARACTERS of that markup, and React escapes them from there.
  check(
    "context leaves markup as text",
    contextText({ component: "<img src=x onerror=alert(1)>" }),
    '{\n  "component": "<img src=x onerror=alert(1)>"\n}',
  );

  // ---- failureDetailState -------------------------------------------------
  check("a detail read in flight", failureDetailState(false, false), "loading");
  // A row swept by retention while the page was open 404s. Spinning for ever
  // is the alternative.
  check("a detail read that failed", failureDetailState(true, false), "failed");
  check("a detail read that arrived", failureDetailState(false, true), "ready");
  check("a failed refresh over a detail still reads", failureDetailState(true, true), "ready");


  // ---- severityDaySeries / disagreeingDays --------------------------------
  const day = (
    d: string,
    warning: number,
    error: number,
    critical: number,
    total?: number,
  ): FailureDayCount => ({
    day: d,
    warning,
    error,
    critical,
    total: total ?? warning + error + critical,
  });
  {
    const days = [day("2026-08-26", 1, 2, 0), day("2026-08-27", 0, 0, 0), day("2026-08-28", 3, 0, 5)];
    const drawn = severityDaySeries(days);
    check("a bar per day, quiet ones included", drawn.labels, [
      "2026-08-26",
      "2026-08-27",
      "2026-08-28",
    ]);
    // THE ORDER. The baseline is the only place a reader can compare across
    // bars, and it belongs to the severity somebody is here to find. Reversing
    // this is invisible in a typecheck and in every count.
    check("critical is on the baseline", drawn.series[0]?.name, "critical");
    check("warning is on top", drawn.series[2]?.name, "warning");
    check("three parts, no more", drawn.series.length, 3);
    check("the critical series", drawn.series[0]?.points.map((p) => p.value), [0, 0, 5]);
    check("the warning series", drawn.series[2]?.points.map((p) => p.value), [1, 0, 3]);
    // A measured zero, never a gap: the server sends every day in the window,
    // so an absent value would be the chart inventing one.
    check("a quiet day is a zero and not a gap", drawn.series[1]?.points[1]?.value, 0);
    check("every point is labelled with its day", drawn.series[1]?.points[2]?.label, "2026-08-28");
    check("a well-formed answer has no disagreement", disagreeingDays(days), []);
  }
  // The parts and the total come off one source on the server, so this can
  // only fire on a document at odds with itself — and drawing the parts under
  // a heading built from the total would hide exactly that.
  check("a total that is not its parts is named", disagreeingDays([day("2026-08-28", 1, 1, 1, 9)]), [
    "2026-08-28",
  ]);
  check("no days, nothing to draw", severityDaySeries([]).labels, []);
  check("no days still names three parts", severityDaySeries([]).series.length, 3);

  // ---- groupPoints --------------------------------------------------------
  {
    const groups: FailureGroupCount[] = [
      { value: "worker", faults: 3, occurrences: 4100 },
      { value: "platform-api", faults: 1, occurrences: 2 },
    ];
    // The two measures are two different numbers about the same rows, and the
    // whole reason `measure` has no default: a chart that picked one silently
    // would be captioned with whichever the reader assumed.
    check("faults", groupPoints(groups, "faults"), [
      { label: "worker", value: 3 },
      { label: "platform-api", value: 1 },
    ]);
    check("occurrences", groupPoints(groups, "occurrences"), [
      { label: "worker", value: 4100 },
      { label: "platform-api", value: 2 },
    ]);
    check("the two measures disagree, which is the point", groupPoints(groups, "faults")[0]?.value !== groupPoints(groups, "occurrences")[0]?.value, true);
    // The response's order is each vocabulary's own, so two windows put their
    // bars in the same places. Nothing here re-ranks it.
    check("the server's order is kept", groupPoints(groups, "faults").map((p) => p.label), [
      "worker",
      "platform-api",
    ]);
  }
  check("nothing produced, nothing drawn", groupPoints([], "faults"), []);

  // ---- sameWindow / windowMismatchNote ------------------------------------
  const unbounded: FailureWindow = { bounded: false, since: null, until: null, days: null, clamped: false };
  const august: FailureWindow = { bounded: true, since: "2026-08-01", until: "2026-08-28", days: 28, clamped: false };
  const july: FailureWindow = { bounded: true, since: "2026-07-01", until: "2026-07-31", days: 31, clamped: false };
  check("a window is itself", sameWindow(august, august), true);
  check("two windows over the same days agree", sameWindow(august, { ...august, days: 99, clamped: true }), true);
  check("different days disagree", sameWindow(august, july), false);
  // THE CASE THE FUNCTION EXISTS FOR: no dates in the URL at all. The list has
  // no window and the summary has thirty days, and they look identical on
  // screen.
  check("bounded and unbounded are not the same window", sameWindow(unbounded, august), false);
  check("agreeing windows say nothing", windowMismatchNote(august, august), null);
  {
    const note = windowMismatchNote(unbounded, august);
    check("a mismatch is said out loud", note === null, false);
    check("it names the charts' days", note?.includes("2026-08-01 to 2026-08-28"), true);
    check("and what the table covers", note?.includes("every fault on record"), true);
  }

  // ---- failureChartState --------------------------------------------------
  check("no summary yet", failureChartState(false, false, 0), "loading");
  check("a summary that failed", failureChartState(true, false, 0), "failed");
  // NOT "nothing is failing". The summary always resolves a window, so an
  // empty one is a claim about thirty days and about nothing else — and the
  // table beside it is routinely full.
  check("an empty window is quiet, not healthy", failureChartState(false, true, 0), "quiet");
  check("faults are drawable", failureChartState(false, true, 4), "ready");
  check("a failed refresh over a summary still draws", failureChartState(true, true, 4), "ready");

  // ---- describeTotals -----------------------------------------------------
  const summaryOf = (over: Partial<FailureSummary>): FailureSummary => ({
    window: august,
    acknowledged: "false",
    application: null,
    faults: 0,
    occurrences: 0,
    counts_basis: "occurred_at",
    occurrences_basis: "lifetime",
    days: [],
    services: [],
    kinds: [],
    severities: [],
    top_fingerprints: [],
    top_fingerprints_truncated: false,
    row_is: "fault",
    identity_visible: true,
    ...over,
  });
  {
    const totals = describeTotals(summaryOf({ faults: 12, occurrences: 41000 }));
    check("both figures", totals.includes("12 faults") && totals.includes("41,000 times"), true);
    // The word that stops the second figure being read as "this month".
    check("the lifetime basis is stated", totals.includes("LIFETIME"), true);
    check("one fault reads as one", describeTotals(summaryOf({ faults: 1, occurrences: 1 })).includes("1 fault started"), true);
    check("one firing reads as once", describeTotals(summaryOf({ faults: 1, occurrences: 1 })).includes("fired 1 time in total"), true);
  }

  // ---- clearScope ---------------------------------------------------------
  const failure = (id: string, acknowledged: boolean): Failure => ({
    id,
    fingerprint: `fp-${id}`,
    occurred_at: "2026-08-28T04:11:09Z",
    last_occurred_at: "2026-08-28T04:12:09Z",
    occurrence_count: 3,
    service: "worker",
    component: "queue",
    severity: "error",
    kind: "job_failed",
    error_code: null,
    message: "job ran out of attempts",
    request_id: null,
    inference_request_id: null,
    user_id: null,
    device_id: null,
    application_id: null,
    acknowledged,
    acknowledged_at: acknowledged ? "2026-08-28T05:00:00Z" : null,
    acknowledged_by: acknowledged ? "op-1" : null,
    acknowledged_reason: acknowledged ? "incident_response" : null,
    recurred: false,
  });
  const pageOf = (over: Partial<FailurePage>): FailurePage => ({
    failures: [],
    limit: 50,
    truncated: false,
    acknowledged: "false",
    window: unbounded,
    application: null,
    row_is: "fault",
    identity_visible: true,
    ...over,
  });
  check("no page, no scope", clearScope(undefined, undefined), null);
  // An UNCUT page IS the matching set, so the figure is exact and comes from
  // the rows the operator is looking at.
  check(
    "an uncut page is the answer",
    clearScope(pageOf({ failures: [failure("a", false), failure("b", false)] }), undefined),
    { matched: 2, clears: 2, exact: true, basis: "page" },
  );
  // Matched and cleared are two numbers. A page under `acknowledged=all` holds
  // rows that already carry somebody's decision, and the act writes neither
  // over them nor a count that includes them.
  check(
    "already-cleared rows are matched and not written",
    clearScope(
      pageOf({ acknowledged: "all", failures: [failure("a", false), failure("b", true), failure("c", true)] }),
      undefined,
    ),
    { matched: 3, clears: 1, exact: true, basis: "page" },
  );
  check(
    "an empty page clears nothing",
    clearScope(pageOf({}), undefined),
    { matched: 0, clears: 0, exact: true, basis: "page" },
  );
  // A CUT page cannot count the set, and the summary can only answer for it
  // when it ran over the same days and the same side of the acknowledgement.
  const cut = pageOf({ truncated: true, failures: [failure("a", false)], window: august });
  check(
    "a cut page with an agreeing summary",
    clearScope(cut, summaryOf({ faults: 812, window: august, acknowledged: "false" })),
    { matched: 812, clears: 812, exact: true, basis: "summary" },
  );
  // THE DEFAULT CASE, and the one that must not borrow: the list has no window
  // and the summary has thirty days. Taking the summary's count here would put
  // a month's figure under a sentence about every fault on record.
  check(
    "a cut page whose summary ran over other days",
    clearScope(pageOf({ truncated: true, failures: [failure("a", false)] }), summaryOf({ faults: 812 })),
    { matched: 1, clears: null, exact: false, basis: "floor" },
  );
  check(
    "a cut page whose summary asked the other side",
    clearScope(cut, summaryOf({ faults: 812, window: august, acknowledged: "all" })),
    { matched: 1, clears: null, exact: false, basis: "floor" },
  );
  check(
    "a cut page with no summary at all",
    clearScope(cut, undefined),
    { matched: 1, clears: null, exact: false, basis: "floor" },
  );
  // The cleared side of a matched set writes nothing, and "all" is the only
  // one the summary cannot split — null, never zero, which would be a real
  // count saying the act is pointless.
  check(
    "the cleared side writes nothing",
    clearScope({ ...cut, acknowledged: "true" }, summaryOf({ faults: 9, window: august, acknowledged: "true" }))?.clears,
    0,
  );
  check(
    "both sides at once cannot be split",
    clearScope({ ...cut, acknowledged: "all" }, summaryOf({ faults: 9, window: august, acknowledged: "all" }))?.clears,
    null,
  );

  // ---- describeClearScope / describeSelection -----------------------------
  check(
    "everything matching is open",
    describeClearScope({ matched: 12, clears: 12, exact: true, basis: "page" }),
    "This will clear 12 faults — every row the filter matches, and every one of them is still open.",
  );
  {
    const mixed = describeClearScope({ matched: 30, clears: 12, exact: true, basis: "page" });
    check("both figures, in one sentence", mixed.includes("30 faults") && mixed.includes("12"), true);
    check("and what happens to the rest", mixed.includes("keep it"), true);
    const one = describeClearScope({ matched: 30, clears: 1, exact: true, basis: "page" });
    check("one open row reads as one", one.includes("1 is still open") && one.includes("that one"), true);
    // Both halves of the sentence count, and each has its own singular. A
    // render proof found "the other 1 already carry" here.
    const lonely = describeClearScope({ matched: 2, clears: 1, exact: true, basis: "page" });
    check("one kept row reads as one", lonely.includes("the other one already carries"), true);
    check("and never as a plural", lonely.includes("carry"), false);
  }
  check(
    "nothing left to clear",
    describeClearScope({ matched: 30, clears: 0, exact: true, basis: "page" }).includes("already been dealt with"),
    true,
  );
  check(
    "a set that cannot be split",
    describeClearScope({ matched: 30, clears: null, exact: true, basis: "summary" }).includes("will say how many"),
    true,
  );
  // A FLOOR must not print as a figure. A number that might be wrong on a
  // confirmation is worse than no number.
  {
    const floor = describeClearScope({ matched: 50, clears: null, exact: false, basis: "floor" });
    check("a floor says it is a floor", floor.includes("not knowable"), true);
    check("and says which way", floor.includes("more than the 50"), true);
    check("a floor never claims to be the count", floor.includes("This will clear"), false);
  }
  check("one tick", describeSelection(1), "This will clear the one fault you have ticked.");
  check("many ticks", describeSelection(1234), "This will clear the 1,234 faults you have ticked.");

  // ---- describeAcknowledgement --------------------------------------------
  const ack = (over: Partial<FailureAcknowledgement>): FailureAcknowledgement => ({
    mode: "ids",
    matched: 0,
    acknowledged: 0,
    already_acknowledged: 0,
    missing: 0,
    max_failure_ids: 200,
    max_matched: 10000,
    filter: null,
    application: null,
    ...over,
  });
  check(
    "the plain success",
    describeAcknowledgement(ack({ matched: 9, acknowledged: 9 })),
    "Cleared 9 of the 9 faults the ask matched.",
  );
  // THE THIRD NUMBER. Without it, a colleague having got there first is
  // invisible and "9 cleared" reads as the whole story.
  {
    const shared = describeAcknowledgement(ack({ matched: 12, acknowledged: 9, already_acknowledged: 3 }));
    check("somebody else got there first", shared.includes("3 already carried"), true);
    check("and their decision stands", shared.includes("never overwritten"), true);
  }
  {
    const stale = describeAcknowledgement(ack({ matched: 8, acknowledged: 8, missing: 2 }));
    check("ids that named nothing", stale.includes("2 named no row"), true);
  }
  // Null on the filter form, where a zero would be a real count of something
  // that cannot happen.
  check(
    "a filter cannot miss",
    describeAcknowledgement(ack({ mode: "filter", matched: 4, acknowledged: 4, missing: null })).includes("named no row"),
    false,
  );
  check(
    "an act that wrote nothing says so",
    describeAcknowledgement(ack({ matched: 4, acknowledged: 0, already_acknowledged: 4 })).includes("Nothing was cleared"),
    true,
  );

  // ---- clearRefusal / describeRefusal / overCeiling -----------------------
  // Only the one refusal. Every other failure is the dialog's own error alert,
  // and a banner about narrowing a filter over a 500 would send an operator
  // editing dates while the database is down.
  check("a 500 is not this refusal", clearRefusal(500, "internal", "Internal error", {}), null);
  check("a 403 is not this refusal", clearRefusal(403, "permission_denied", "no", {}), null);
  check(
    "a 422 of another code is not this refusal",
    clearRefusal(422, "conflict", "no", {}),
    null,
  );
  {
    const refusal = clearRefusal(422, "failed_precondition", "That filter matches 45000 failures", {
      code: "failed_precondition",
      matched: 45000,
      max_matched: 10000,
    });
    check("the refusal is recognised", refusal?.matched, 45000);
    check("with the ceiling on it", refusal?.ceiling, 10000);
    const words = refusal ? describeRefusal(refusal) : "";
    check("the count is in the sentence", words.includes("45,000 faults"), true);
    check("so is the ceiling", words.includes("10,000"), true);
    // The fact an operator most needs and would otherwise assume the other way
    // round: a refused bulk act is not a partial one.
    check("and that nothing happened", words.includes("NOTHING WAS WRITTEN"), true);
  }
  // A proxy's error page classifies as a problem document with no members at
  // all. A banner built on `undefined` would read "matches faults, more than a".
  {
    const bare = clearRefusal(422, "failed_precondition", "Refused.", undefined);
    check("a document with no members still parses", bare?.matched, null);
    const words = bare ? describeRefusal(bare) : "";
    check("and reads as a sentence", words.startsWith("This filter matches more faults than the ceiling."), true);
    check("undefined never reaches the screen", words.includes("undefined"), false);
    check("nor NaN", words.includes("NaN"), false);
  }
  check("a member of the wrong type is absent", clearRefusal(422, "failed_precondition", "x", { matched: "lots" })?.matched, null);
  // The ceiling is only knowable from a response, so nothing is over it until
  // one has arrived.
  check("no ceiling, no warning", overCeiling({ matched: 99999, clears: null, exact: true, basis: "summary" }, null), false);
  check("no scope, no warning", overCeiling(null, 10000), false);
  check("under the ceiling", overCeiling({ matched: 10000, clears: 10000, exact: true, basis: "summary" }, 10000), false);
  check("over the ceiling", overCeiling({ matched: 10001, clears: 10001, exact: true, basis: "summary" }, 10000), true);
  // A floor is enough: "more than N" is over the ceiling whenever N is.
  check("a floor over the ceiling still warns", overCeiling({ matched: 20000, clears: null, exact: false, basis: "floor" }, 10000), true);

  // ---- the selection ------------------------------------------------------
  const rows = [failure("a", false), failure("b", true), failure("c", false)];
  // A cleared row has no tick box: the act would write nothing, and it would
  // make "the exact number affected" a number the screen has to qualify.
  check("only open rows can be ticked", selectableIDs(rows), ["a", "c"]);
  check("a page of cleared rows offers nothing", selectableIDs([failure("b", true)]), []);
  check("ticks in row order", effectiveSelection(new Set(["c", "a"]), rows), ["a", "c"]);
  // THE ONE THIS EXISTS FOR: the list refetches after every acknowledgement,
  // and a tick surviving on a row that is no longer on screen would clear a
  // fault nobody looked at.
  check("a tick on a row that has gone is dropped", effectiveSelection(new Set(["a", "zzz"]), rows), ["a"]);
  check("a tick on a row somebody else cleared is dropped", effectiveSelection(new Set(["b"]), rows), []);
  check("adding a tick", [...toggleSelected(new Set(["a"]), "c", true)].sort(), ["a", "c"]);
  check("removing a tick", [...toggleSelected(new Set(["a", "c"]), "a", false)], ["c"]);
  check("removing one that was never there", [...toggleSelected(new Set(["a"]), "z", false)], ["a"]);
  check("the header with nothing ticked", selectAllState(0, 3), "none");
  check("the header part way", selectAllState(2, 3), "some");
  check("the header full", selectAllState(3, 3), "all");
  // A page with nothing selectable is `none`, never `all`: a control claiming
  // to have selected everything when there is nothing to select is a button
  // that does nothing when pressed.
  check("nothing selectable is never all", selectAllState(0, 0), "none");
  // The list's page ceiling and the id ceiling are the same number on the
  // server, so this cannot be exceeded from a page — asserted rather than
  // assumed, because the day they stop agreeing the control starts 400ing.
  check("a full page is within the ceiling", selectionWithinCeiling(200, 200), true);
  check("past the ceiling", selectionWithinCeiling(201, 200), false);
  check("an unknown ceiling refuses nothing", selectionWithinCeiling(9999, null), true);

  // ---- the words both controls share --------------------------------------
  // The operator's last chance to learn that this is not a delete, so it says
  // the word rather than implying it.
  check("clearing is not a delete", CLEARING_HIDES.includes("does not delete"), true);
  check("and says what to do to see them again", CLEARING_HIDES.includes("Open and cleared"), true);
  // The two grants are two strings, and the compiler already refuses to
  // compare them — so what is worth pinning is the VALUE, which has to match
  // `PermFailuresClear` in internal/admin/rbac.go. A typo here hides every
  // clear control from every operator, silently, because `can` simply never
  // matches.
  check("the clear grant is the server's", FAILURES_CLEAR, "failures:clear");
  check("the read grant is the server's", FAILURES_READ, "failures:read");

  console.log(`failure-log self-check: ${passed} checks passed`);
}

if ((import.meta as unknown as { main?: boolean }).main === true) {
  selfCheck();
}
